'use client';

import { createContext, useContext, type PropsWithChildren } from 'react';

import type { UseAuth } from '@/hooks/auth/types';
import useClassicAuth from '@/hooks/auth/useClassicAuth';
import useSleepAuth from '@/hooks/auth/useSleepAuth';

export type AuthMode = 'classic' | 'sleep';

const AuthContext = createContext<UseAuth | undefined>(undefined);

function ClassicAuthProvider({ children }: PropsWithChildren) {
	const auth = useClassicAuth();
	return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

function SleepAuthProvider({ children }: PropsWithChildren) {
	const auth = useSleepAuth();
	return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export default function AuthProvider({ mode, children }: PropsWithChildren<{ mode: AuthMode }>) {
	return mode === 'sleep' ? (
		<SleepAuthProvider>{children}</SleepAuthProvider>
	) : (
		<ClassicAuthProvider>{children}</ClassicAuthProvider>
	);
}

export function useAuth(): UseAuth {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error('useAuth must be used within <AuthProvider>');
	}
	return auth;
}
